import { AlertTriangle, ArrowRight, HelpCircle } from 'lucide-react';
import { Link } from 'react-router';

import {
  type EscolhaClassificacao,
  escolhaDoValor,
  opcoesDeClassificacao,
  valorDaClassificacao,
} from '@/lib/classificacao-personalizada';
import { cx } from '@/lib/cx';
import { Carimbo } from '@/ui/components/carimbo';
import { SelectPesquisavel } from '@/ui/components/select-pesquisavel';
import { Badge } from '@/ui/primitives/badge';
import { Dica } from '@/ui/primitives/tooltip';
import {
  type CfopEntrada,
  type ClassificacaoDoItem,
  type ClassificacaoPersonalizada,
} from '@fiscoai/contracts';

import { rotuloCentroCusto } from './api-centro-custo';
import { CLASSIFICACAO, GRUPO_CONTABIL } from './rotulos';
import { SeletorCentroCusto } from './seletor-centro-custo';

/** CFOP do fornecedor → CFOP de entrada; sem regra segura, diz isso (nunca chuta). */
function CfopDeEntrada({ cfop, entrada }: { cfop: string; entrada: CfopEntrada | null }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <span className="font-mono">{cfop}</span>
      <ArrowRight className="size-3.5 text-texto-fraco" aria-label="vira" />
      {entrada === null ? (
        <span className="text-texto-fraco">—</span>
      ) : entrada.tipo === 'SEM_REGRA' ? (
        <Dica texto={entrada.motivo}>
          <span
            className="inline-flex items-center gap-1 text-alerta"
            aria-label={entrada.motivo}
            tabIndex={0}
          >
            sem regra — classificar manualmente
            <HelpCircle className="size-3.5" aria-hidden />
          </span>
        </Dica>
      ) : (
        <span className="font-mono font-semibold text-marca">{entrada.cfop}</span>
      )}
    </span>
  );
}

/**
 * "pela regra Combustíveis": a classificação veio de uma regra da empresa (spec 0007). Leva às
 * regras; escolher outra classificação à mão grava MANUAL e o servidor deixa de mandar a regra.
 */
function CarimboRegra({ item, empresaId }: { item: ClassificacaoDoItem; empresaId: string }) {
  if (item.origemClassificacao !== 'REGRA' || !item.regra) return null;
  return (
    <Link
      to={`/empresas/${empresaId}?aba=regras`}
      aria-label={`Classificado pela regra ${item.regra.nome}: abrir as regras da empresa`}
      className="inline-flex items-center rounded-mini px-1 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave"
    >
      <Carimbo tom="info">{`pela regra ${item.regra.nome}`}</Carimbo>
    </Link>
  );
}

/** O nome exibido: o da classificação da empresa quando é personalizada. */
function nomeDaClassificacao(item: ClassificacaoDoItem): string {
  if (item.personalizada) return item.personalizada.nome;
  return item.classificacao ? CLASSIFICACAO[item.classificacao] : 'Sem classificação';
}

interface Props {
  cfopFornecedor: string;
  item: ClassificacaoDoItem;
  podeEditar: boolean;
  /** Acabou de mudar: destaca uma vez (respeita reduced motion pelo token). */
  destacar: boolean;
  empresaId: string;
  /** Classificações da empresa (spec 0009): as ativas entram no grupo "Desta empresa". */
  personalizadas: readonly ClassificacaoPersonalizada[];
  aoClassificar: (escolha: EscolhaClassificacao) => void;
  aoDefinirCentroCusto: (centroCustoId: string | null) => void;
}

/**
 * Faixa fiscal do item: classificação, centro de custo, CFOP de entrada, grupo contábil,
 * patrimônio e alertas.
 */
export function FaixaFiscal({
  cfopFornecedor,
  item,
  podeEditar,
  destacar,
  empresaId,
  personalizadas,
  aoClassificar,
  aoDefinirCentroCusto,
}: Props) {
  return (
    <div
      className={cx(
        'grid gap-2 rounded-controle bg-superficie-afundada px-3 py-2',
        destacar && 'animate-destaque',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        {podeEditar ? (
          <div className="flex flex-wrap items-center gap-2">
            <SelectPesquisavel
              aria-label={`Classificação do item ${String(item.nItem)}`}
              rotuloBusca="Buscar classificação"
              className="w-60"
              tamanho="sm"
              placeholder="Classificar item"
              opcoes={opcoesDeClassificacao(personalizadas, item.personalizada)}
              valor={valorDaClassificacao(item.classificacao, item.personalizada)}
              aoMudar={(v) => {
                if (v) aoClassificar(escolhaDoValor(v));
              }}
            />
            <SeletorCentroCusto
              aria-label={`Centro de custo do item ${String(item.nItem)}`}
              placeholder="Centro de custo"
              className="w-60"
              tamanho="sm"
              empresaId={empresaId}
              valor={item.centroCusto}
              aoMudar={aoDefinirCentroCusto}
            />
            <CarimboRegra item={item} empresaId={empresaId} />
          </div>
        ) : (
          <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {nomeDaClassificacao(item)}
            <CarimboRegra item={item} empresaId={empresaId} />
          </span>
        )}
        <CfopDeEntrada cfop={cfopFornecedor} entrada={item.cfopEntrada} />
      </div>
      <Selos item={item} mostrarCentroCusto={!podeEditar} />
      <Alertas alertas={item.alertas} />
    </div>
  );
}

function OrigemDoGrupo({ origem }: { origem: ClassificacaoDoItem['origemGrupo'] }) {
  if (origem === 'CENTRO_CUSTO') return <Badge tom="neutro">pelo centro de custo</Badge>;
  if (origem === 'PADRAO_SEM_CC') return <Badge tom="alerta">padrão sem centro de custo</Badge>;
  return null;
}

/** Grupo com a origem (pelo centro de custo ou padrão sem ele), CC (só leitura) e patrimônio. */
function Selos({
  item,
  mostrarCentroCusto,
}: {
  item: ClassificacaoDoItem;
  mostrarCentroCusto: boolean;
}) {
  const cc = mostrarCentroCusto ? item.centroCusto : null;
  if (item.grupoContabil === null && !item.patrimonio && !cc) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {item.grupoContabil && <Badge tom="marca">{GRUPO_CONTABIL[item.grupoContabil]}</Badge>}
      <OrigemDoGrupo origem={item.origemGrupo} />
      {cc && <Badge tom="neutro">CC {rotuloCentroCusto(cc)}</Badge>}
      {item.centroCusto?.ativo === false && <Badge tom="alerta">CC inativo</Badge>}
      {item.patrimonio && <Badge tom="info">Patrimônio</Badge>}
    </div>
  );
}

function Alertas({ alertas }: { alertas: readonly string[] }) {
  if (alertas.length === 0) return null;
  return (
    <ul className="grid gap-1">
      {alertas.map((a) => (
        <li key={a} className="flex items-start gap-1.5 text-xs text-alerta">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {a}
        </li>
      ))}
    </ul>
  );
}
